import { CameraPerformance } from 'domain/camera-performance'
import { VehicleCounts } from 'domain/vehicle-counts'
import { Carousel } from 'primereact/carousel'
import { useState } from 'react'
import CameraCaptureCounter from './dashboard-camera-counter/dashboard-camera-counter'
import { CameraEvent } from '../types/camera-event'
import NotificationGrid from './dashboard-notifications/dashboard-notification-grid/dashboard-notification-grid'
import VehicleTypeCounter from './dashboard-vehicle-type-counter/dashboard-vehicle-type-counter'
import './dashboard-view-mobile.scss'

interface DashboardViewMobileProps {
  cameraPerformance: CameraPerformance[]
  yesterdayCameraPerformance?: CameraPerformance[]
  vehicleCount: VehicleCounts
  eventHistory: CameraEvent[]
}

type PanelKind = 'cameras' | 'categories' | 'events'

interface DashboardPanel {
  key: string
  kind: PanelKind
  label: string
  render: () => React.ReactNode
}

// Câmeras por slide de paginação, no mobile — mantém o tamanho do card
// constante independente de quantas câmeras existirem.
const CAMERAS_PER_PAGE = 6

function chunk<T>(items: T[], size: number): T[][] {
  if (items.length === 0) return [[]]
  const pages: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    pages.push(items.slice(i, i + size))
  }
  return pages
}

/**
 * Phone layout for the dashboard: the same three blocks as the desktop grid,
 * but one screen at a time and swipeable, so it reads as an app rather than a
 * very long scroll. Only the events panel scrolls — cameras/categories are
 * static screens sized to fit (see the stylesheet).
 */
const DashboardViewMobile = ({
  cameraPerformance,
  yesterdayCameraPerformance,
  vehicleCount,
  eventHistory,
}: DashboardViewMobileProps) => {
  const [page, setPage] = useState(0)

  const cameraPages = chunk(cameraPerformance, CAMERAS_PER_PAGE)
  const totalCaptures = cameraPerformance.reduce(
    (acc, camera) => acc + camera.eventCount,
    0
  )

  const panels: DashboardPanel[] = [
    ...cameraPages.map(
      (cameras, i): DashboardPanel => ({
        key: `cameras-${i}`,
        kind: 'cameras',
        label: cameraPages.length > 1 ? `Câmeras ${i + 1}` : 'Câmeras',
        render: () => (
          <CameraCaptureCounter
            cameraPerformance={cameras}
            yesterdayCameraPerformance={yesterdayCameraPerformance}
            totalCaptures={totalCaptures}
            pageLabel={
              cameraPages.length > 1
                ? `${i + 1} de ${cameraPages.length}`
                : undefined
            }
          />
        ),
      })
    ),
    {
      key: 'categories',
      kind: 'categories',
      label: 'Categorias',
      render: () => <VehicleTypeCounter vehicleCounts={vehicleCount} />,
    },
    {
      key: 'events',
      kind: 'events',
      label: 'Eventos',
      render: () => <NotificationGrid eventHistory={eventHistory} />,
    },
  ]

  // Clamp against transient out-of-range pages (e.g. the camera list shrinks
  // across a SignalR update and removes a page the user was on).
  const activePage = Math.min(page, panels.length - 1)

  return (
    <div className='dashboard-mobile-body'>
      <Carousel
        value={panels}
        page={activePage}
        onPageChange={(e: { page: number }) => setPage(e.page)}
        numVisible={1}
        numScroll={1}
        showNavigators={false}
        showIndicators={false}
        className='dashboard-mobile-carousel'
        itemTemplate={(panel: DashboardPanel) => (
          <div className={`dashboard-mobile-panel dashboard-mobile-panel--${panel.kind}`}>
            {panel.render()}
          </div>
        )}
      />

      {/* Custom footer: the app's own brand mark (fixed, doesn't change with
          the slide) plus the page dots — PrimeReact's built-in indicators sit
          right under the cards instead of anchored to the screen's bottom. */}
      <div className='dashboard-mobile-footer'>
        <div className='dashboard-mobile-brand'>
          <img
            src='/favicon.png'
            alt=''
            className='dashboard-mobile-brand-icon'
          />
          <span className='dashboard-mobile-brand-text'>RT GUARD</span>
        </div>
        <div
          className='dashboard-mobile-dots'
          role='tablist'
          aria-label='Páginas do dashboard'
        >
          {panels.map((panel, i) => (
            <button
              key={panel.key}
              type='button'
              role='tab'
              aria-selected={i === activePage}
              aria-label={panel.label}
              className={`dashboard-mobile-dot${i === activePage ? ' dashboard-mobile-dot--active' : ''}`}
              onClick={() => setPage(i)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default DashboardViewMobile
